/**
 * Form Validation Utilities
 * 
 * DRY principle - Tüm form validasyonları bu dosyada merkezileştirilmiştir.
 * Her validator fonksiyonu null (valid) veya error string (invalid) döner.
 */

/**
 * Email format validation
 * @param {string} value - Email address to validate
 * @returns {string|null} Error message or null if valid
 */
const email = (value) => {
  if (!value) return 'Email gereklidir';
  if (!/\S+@\S+\.\S+/.test(value)) return 'Geçersiz email formatı';
  return null;
};

/**
 * Required field validation
 * @param {string} value - Field value to validate
 * @param {string} fieldName - Display name for error message
 * @returns {string|null} Error message or null if valid
 */
const required = (value, fieldName) => {
  if (!value || !value.trim()) return `${fieldName} gereklidir`;
  return null;
};

/**
 * Minimum length validation
 * @param {string} value - Field value to validate
 * @param {number} min - Minimum required length
 * @param {string} fieldName - Display name for error message
 * @returns {string|null} Error message or null if valid
 */
const minLength = (value, min, fieldName) => {
  if (!value) return `${fieldName} gereklidir`;
  if (value.length < min) return `${fieldName} en az ${min} karakter olmalıdır`;
  return null;
};

/**
 * Password strength validation
 * Phase 2 minimal rules:
 * - Minimum 8 characters
 * - At least 1 uppercase letter
 * - At least 1 number
 * - NO special character requirement (Phase 2 minimal)
 * 
 * @param {string} value - Password to validate
 * @returns {string|null} Error message or null if valid
 */
const password = (value) => {
  if (!value) return 'Şifre gereklidir';
  
  const errors = [];
  if (value.length < 8) errors.push('en az 8 karakter');
  if (!/[A-Z]/.test(value)) errors.push('en az 1 büyük harf');
  if (!/[0-9]/.test(value)) errors.push('en az 1 rakam');
  
  if (errors.length > 0) {
    return `Şifre ${errors.join(', ')} içermelidir`;
  }
  
  return null;
};

/**
 * Password match validation
 * @param {string} password - Original password
 * @param {string} confirm - Confirmation password
 * @returns {string|null} Error message or null if valid
 */
const passwordMatch = (password, confirm) => {
  if (!confirm) return 'Şifre tekrarı gereklidir';
  if (password !== confirm) return 'Şifreler eşleşmiyor';
  return null;
};

/**
 * Validators object - Export edilecek tüm validator fonksiyonları
 */
export const validators = {
  email,
  required,
  minLength,
  password,
  passwordMatch
};

/**
 * Generic form validation runner
 * 
 * Tüm validation kurallarını çalıştırır ve error object döner.
 * 
 * @param {Object} formData - Form data object to validate
 * @param {Object} rules - Validation rules object
 *   Format: { fieldName: (value, formData) => errorString | null }
 * @returns {Object} Errors object { fieldName: errorString }
 * 
 * @example
 * const errors = validateForm(
 *   { email: 'test', password: '123' },
 *   {
 *     email: validators.email,
 *     password: validators.password
 *   }
 * );
 * // Returns: { email: 'Geçersiz email formatı', password: 'Şifre en az 8 karakter...' }
 */
export const validateForm = (formData, rules) => {
  const errors = {};
  
  Object.keys(rules).forEach((field) => {
    const validator = rules[field];
    const error = validator(formData[field], formData);
    
    if (error) {
      errors[field] = error;
    }
  });
  
  return errors;
};

export default validators;

